/**
 * How much file content one sync may load into Pyodide (EHQ-548).
 *
 * A sync fetched every workflow and project file at once and mounted them all,
 * with no limit, so a project with large files kept every byte resident in the
 * tab (the fetched content and the MEMFS copy). A file is charged its listed
 * size before it is fetched, so a listed file that doesn't fit is never
 * downloaded. A file listed without a size can only be charged once its content
 * arrives, so callers fetch those one at a time: one file over the budget can be
 * held briefly, never all of them.
 */
export class PythonFileBudget {
  #used = 0;
  readonly #skipped: string[] = [];

  constructor(
    private readonly maxFileSize?: number,
    private readonly maxTotalSize?: number,
  ) {}

  /** Files that didn't fit, as "name: reason". */
  get skipped(): readonly string[] {
    return this.#skipped;
  }

  /** Charges `size` bytes for `name`; false, with the reason recorded, when it doesn't fit. */
  admit(name: string, size: number): boolean {
    if (this.maxFileSize && size > this.maxFileSize) {
      this.#skipped.push(`${name}: larger than the ${formatSize(this.maxFileSize)} limit per file`);
      return false;
    }
    if (this.maxTotalSize && this.#used + size > this.maxTotalSize) {
      this.#skipped.push(`${name}: over the ${formatSize(this.maxTotalSize)} limit for all files in a Python step`);
      return false;
    }
    this.#used += size;
    return true;
  }
}

/**
 * Byte size of fetched file content. A string counts its UTF-8 bytes, which is
 * what the server lists and what MEMFS stores, not its UTF-16 length.
 */
export function fileContentSize(content: unknown): number {
  if (typeof content === 'string') return utf8Length(content);
  if (content instanceof ArrayBuffer || ArrayBuffer.isView(content)) return content.byteLength;
  if (content instanceof Blob) return content.size;
  return 0;
}

/** UTF-8 byte length without encoding a copy of the string. */
function utf8Length(text: string): number {
  let bytes = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && (text.charCodeAt(i + 1) & 0xfc00) === 0xdc00) {
      // A surrogate pair is one 4-byte character.
      bytes += 4;
      i++;
    } else bytes += 3;
  }
  return bytes;
}

/** A limit as the user reads it: MB, or KB or bytes for a limit under 1 MB. */
function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}
